import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import AdminCourseGrants from './AdminCourseGrants';

const fmtDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

export default function AdminUsersOrders() {
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [demo, setDemo] = useState(null);
  const [demoPassword, setDemoPassword] = useState('');

  const loadDemo = () => api.get('/admin/partner-demo').then(({ data }) => setDemo(data)).catch(() => setDemo(null));

  useEffect(() => {
    api.get('/admin/users').then(({ data }) => setUsers(data.items)).catch(() => {});
    api.get('/admin/orders').then(({ data }) => setOrders(data.items)).catch(() => {});
    loadDemo();
  }, []);

  const demoAction = async (path, payload) => {
    try {
      await api.post(`/admin/partner-demo/${path}`, payload);
      setDemoPassword('');
      await loadDemo();
      toast.success('Operazione Demo Partner completata');
    } catch (err) { toast.error(err.response?.data?.detail || 'Operazione non riuscita'); }
  };

  return (
    <div data-testid="admin-users-orders">
      <Tabs defaultValue="users">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start">
          <TabsTrigger value="users" data-testid="tab-users">Utenti ({users.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Ordini ({orders.length})</TabsTrigger>
          <TabsTrigger value="demo" data-testid="tab-partner-demo">Demo Partner</TabsTrigger>
          <TabsTrigger value="grants">Accessi inclusi</TabsTrigger>
        </TabsList>
        <TabsContent value="grants"><AdminCourseGrants users={users} /></TabsContent>
        <TabsContent value="users">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  {['Nome', 'Email', 'Ruolo', 'Acquisti', 'Registrato'].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <td className="px-5 py-3.5 font-medium text-slate-800">{u.name}</td>
                    <td className="px-5 py-3.5 text-slate-600">{u.email}</td>
                    <td className="px-5 py-3.5">
                      <Badge className={u.role === 'ADMIN' ? 'bg-brand text-white' : 'bg-slate-100 text-slate-600'}>{u.role}</Badge>
                    </td>
                    <td className="px-5 py-3.5 text-slate-800 font-semibold">{u.purchases_count}</td>
                    <td className="px-5 py-3.5 text-slate-500">{fmtDateTime(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="orders">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  {['Data', 'Utente', 'Concorso', 'Importo', 'Stato'].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((o) => (
                  <tr key={o.session_id} data-testid={`order-row-${o.session_id}`}>
                    <td className="px-5 py-3.5 text-slate-500">{fmtDateTime(o.created_at)}</td>
                    <td className="px-5 py-3.5 text-slate-600">{o.user_email || '—'}</td>
                    <td className="px-5 py-3.5 text-slate-700 max-w-[240px] truncate">{o.contest_title || '—'}</td>
                    <td className="px-5 py-3.5 font-semibold text-slate-800">€ {(o.amount / 100).toFixed(2)}</td>
                    <td className="px-5 py-3.5">
                      <Badge className={o.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 border' : 'bg-slate-100 text-slate-500'}>
                        {o.payment_status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="demo">
          <div className="max-w-2xl rounded-2xl border border-slate-200 bg-white p-6" data-testid="partner-demo-admin">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div><p className="text-xs font-bold uppercase tracking-wider text-brand">Demo Partner</p><h2 className="mt-1 font-display text-xl font-bold">{demo?.email || 'demo-partner@concorsiessentials.it'}</h2></div>
              <Badge className={demo?.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{demo?.enabled ? 'ENABLED' : 'DISABLED'}</Badge>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              <div><p className="text-slate-500">Sessioni</p><p className="font-bold">{demo?.active_sessions ?? 0}/{demo?.max_sessions ?? 30}</p></div>
              <div><p className="text-slate-500">Ultimo accesso</p><p className="font-bold">{fmtDateTime(demo?.last_access)}</p></div>
              <div><p className="text-slate-500">Corso Demo</p><p className="font-bold">{demo?.course_ready ? 'READY' : 'NON CONFIGURATO'}</p></div>
              <div><p className="text-slate-500">Moduli</p><p className="font-bold">{demo?.selected_module_ids?.length || 0}</p></div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {demo?.configured && <Button variant="outline" onClick={async () => { await api.patch('/admin/partner-demo/enabled', { enabled: !demo.enabled }); loadDemo(); }}>{demo.enabled ? 'Disabilita' : 'Abilita'}</Button>}
              {demo?.configured && <Button variant="outline" onClick={() => demoAction('terminate-sessions')}>Termina tutte le sessioni</Button>}
              {demo?.configured && <Button variant="outline" onClick={() => demoAction('reset-state')}>Reset stato Demo</Button>}
            </div>
            <div className="mt-6 border-t border-slate-200 pt-6">
              <label htmlFor="demo-password" className="text-sm font-semibold">{demo?.configured ? 'Nuova password Demo' : 'Password iniziale Demo'}</label>
              <div className="mt-2 flex gap-2"><Input id="demo-password" type="password" minLength={12} value={demoPassword} onChange={(e) => setDemoPassword(e.target.value)} autoComplete="new-password" />
                <Button disabled={demoPassword.length < 12} onClick={() => demoAction(demo?.configured ? 'reset-password' : 'provision', { password: demoPassword })}>{demo?.configured ? 'Reset password' : 'Configura Demo'}</Button>
              </div>
              <p className="mt-2 text-xs text-slate-500">La password non viene salvata nel codice e il reset termina tutte le sessioni attive.</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
